<template>
  <div class="ve-input">
    <FormInput
      ref="formInput"
      class="field-mapping-dialog__code-input"
      :label="label"
      :is-required="isRequired"
      :is-textarea="isTextarea"
      :rows="2"
      has-gray-background
      :value="value"
      :hint="hint"
      :error="error"
      @input="onInput"
    >
      <template #top-right>
        <slot name="top-right" />
      </template>
    </FormInput>

    <!-- Syntax-highlight overlay: sits on top of the (transparent) input. `pointer-events: none` lets clicks/typing through. -->
    <div
      v-show="highlightActive"
      ref="highlight"
      class="ve-input__highlight"
      :style="highlightStyle"
      aria-hidden="true"
    >
      <span v-for="(part, i) in highlightParts" :key="i" :class="{ 've-input__highlight-dynamic': part.dynamic }">{{
        part.text
      }}</span
      ><!-- trailing zero-width space so the overlay grows to match a trailing newline -->&#8203;
    </div>

    <!-- `mousedown.prevent` keeps clicks from blurring the input. `position: fixed` (set inline) escapes the dialog's overflow clip. -->
    <div v-if="showDropdown" class="ve-input__dropdown" :style="dropdownStyle" @mousedown.prevent>
      <div class="ve-input__dropdown-list" role="listbox">
        <div
          v-for="(item, idx) in suggestions"
          :key="item.id"
          role="option"
          :aria-selected="idx === activeIndex"
          :class="['ve-input__item', { 've-input__item--active': idx === activeIndex }]"
          @mouseenter="activeIndex = idx"
          @click="applySuggestion(idx)"
        >
          <code class="ve-input__item-label">{{ item.label }}</code>
          <span v-if="item.availability" class="ve-input__item-badge">{{ item.availability }}</span>
          <span v-if="item.typeLabel" class="ve-input__item-type">{{ item.typeLabel }}</span>
        </div>
      </div>
      <div v-if="activeDescription" class="ve-input__description">{{ activeDescription }}</div>
      <div v-else-if="existingHint" class="ve-input__description ve-input__description--hint">
        {{ existingHint }}
      </div>
    </div>
  </div>
</template>

<script>
import FormInput from '@/components/form/FormInput';
import { mapActions, mapGetters } from 'vuex';
import { getAutocompleteState, buildSuggestions } from '@/util/value-expression-parser';

export default {
  name: 'ValueExpressionInput',
  components: { FormInput },
  props: {
    value: { type: String, default: '' },
    label: { type: String, default: '' },
    isRequired: { type: Boolean, default: false },
    hint: { type: String, default: '' },
    error: { type: String, default: null },
    // Renders as a textarea (multi-line) when true; single-line input otherwise.
    isTextarea: { type: Boolean, default: false },
  },
  data() {
    return {
      cursor: 0,
      activeIndex: 0,
      focused: false,
      // Viewport-relative position for the fixed dropdown; recomputed on focus/input/resize/scroll.
      dropdownStyle: { display: 'none' },
      // Inline style for the overlay, populated by syncHighlight() from getComputedStyle.
      highlightStyle: {},
      highlightActive: false,
    };
  },
  computed: {
    ...mapGetters('valueExpressionSchema', { schema: 'schema', schemaLoaded: 'loaded' }),
    availabilityLabels() {
      return {
        TENDER_UPDATE_ONLY: this.$t('page.admin-sync-profiles.field-mapping-help.availability-tender-update'),
        BC_PATCH_ONLY: this.$t('page.admin-sync-profiles.field-mapping-help.availability-bc'),
        DYNAMICS_ONLY: this.$t('page.admin-sync-profiles.field-mapping-help.availability-dynamics'),
      };
    },
    parseState() {
      if (!this.schemaLoaded || !this.schema) return null;
      return getAutocompleteState(this.value || '', this.cursor, this.schema);
    },
    suggestions() {
      if (!this.parseState) return [];
      return buildSuggestions(this.parseState, this.schema, { availabilityLabels: this.availabilityLabels });
    },
    showDropdown() {
      if (!this.focused) return false;
      if (this.suggestions.length > 0) return true;
      // `existing.<column>` — suppress autocomplete but show a one-line hint.
      if (this.parseState && this.parseState.inExistingMap) return true;
      return false;
    },
    existingHint() {
      if (this.parseState && this.parseState.inExistingMap) {
        return this.$t('page.admin-sync-profiles.field-mapping-help.existing-column-hint');
      }
      return null;
    },
    activeDescription() {
      const item = this.suggestions[this.activeIndex];
      return (item && item.description) || '';
    },
    // Splits the value into alternating literal / `{{...}}` parts. Non-greedy so empty `{{}}`
    // and adjacent `{{a}}{{b}}` tokenize correctly.
    highlightParts() {
      const expr = this.value || '';
      if (!expr) return [];
      const re = /\{\{[^}]*?\}\}/g;
      const parts = [];
      let lastIndex = 0;
      let match;
      while ((match = re.exec(expr)) !== null) {
        if (match.index > lastIndex) {
          parts.push({ text: expr.slice(lastIndex, match.index), dynamic: false });
        }
        parts.push({ text: match[0], dynamic: true });
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < expr.length) {
        parts.push({ text: expr.slice(lastIndex), dynamic: false });
      }
      return parts;
    },
  },
  watch: {
    suggestions(newList, oldList) {
      if (newList.length === 0) return;
      // Preserve the highlighted item across re-renders.
      const oldId = oldList[this.activeIndex] && oldList[this.activeIndex].id;
      const idx = oldId ? newList.findIndex((s) => s.id === oldId) : -1;
      this.activeIndex = idx >= 0 ? idx : 0;
      // Suggestion count changed → recompute placement.
      this.$nextTick(() => this.positionDropdown());
    },
    showDropdown(visible) {
      if (visible) this.$nextTick(() => this.positionDropdown());
    },
    value() {
      // Text changed — the textarea may have grown; re-sync the overlay.
      this.$nextTick(() => this.syncHighlight());
    },
    isTextarea() {
      // The underlying FormInput swaps between <textarea> and <input>, replacing the DOM
      // node. Vuetify v-dialog keeps this component mounted across open/close (bootable
      // mixin: isBooted stays true), so without re-binding, listeners stay attached to
      // the orphaned old element and the new element gets nothing → autocomplete dies.
      this.$nextTick(() => {
        this.attachInputListeners();
        this.syncHighlight();
      });
    },
  },
  mounted() {
    // Store-cached; a no-op after first fetch.
    this.loadSchema().catch(() => {
      // Logged in the module; degrade silently.
    });

    // Bind handler functions once; they read `this.inputEl()` lazily so they stay valid
    // when the underlying element changes identity (see attachInputListeners).
    this._onFocus = () => {
      this.focused = true;
      this.refreshCursor();
      this.$nextTick(() => this.positionDropdown());
    };
    this._onBlur = () => {
      // Defer so a click on a suggestion can fire before we close.
      setTimeout(() => {
        this.focused = false;
      }, 120);
    };
    this._onKeydown = (event) => this.onKeydown(event);
    // keyup (not keydown) — selectionStart isn't updated until after keydown fires.
    this._onKeyup = () => this.refreshCursor();
    this._onClick = () => this.refreshCursor();
    this._onSelect = () => this.refreshCursor();
    // Mirror the textarea's scroll on the overlay.
    this._onHighlightScroll = () => {
      const overlay = this.$refs.highlight;
      const el = this.inputEl();
      if (overlay && el) {
        overlay.scrollTop = el.scrollTop;
        overlay.scrollLeft = el.scrollLeft;
      }
    };

    this.attachInputListeners();

    // Wait one tick so getComputedStyle sees the final layout, then reveal the overlay.
    this.$nextTick(() => {
      this.syncHighlight();
      this.highlightActive = true;
    });

    // Capture-phase scroll catches the dialog's own overflow scroll too, so the fixed-position
    // dropdown and the overlay both keep tracking the input.
    this._onReflow = () => {
      if (this.showDropdown) this.positionDropdown();
      this.syncHighlight();
    };
    window.addEventListener('resize', this._onReflow);
    window.addEventListener('scroll', this._onReflow, true);
  },
  beforeDestroy() {
    this.detachInputListeners();
    if (this._onReflow) {
      window.removeEventListener('resize', this._onReflow);
      window.removeEventListener('scroll', this._onReflow, true);
    }
    if (this._highlightResizeObserver) this._highlightResizeObserver.disconnect();
  },
  methods: {
    ...mapActions('valueExpressionSchema', ['loadSchema']),
    inputEl() {
      const root = this.$refs.formInput && this.$refs.formInput.$el;
      return root ? root.querySelector('input,textarea') : null;
    },
    // Attach (or re-attach) the input-level listeners + ResizeObserver to the current
    // underlying element. Idempotent — bails when the element hasn't changed.
    attachInputListeners() {
      const el = this.inputEl();
      if (!el || el === this._listenedEl) return;
      this.detachInputListeners();
      this._listenedEl = el;
      el.addEventListener('focus', this._onFocus);
      el.addEventListener('blur', this._onBlur);
      el.addEventListener('keydown', this._onKeydown);
      el.addEventListener('keyup', this._onKeyup);
      el.addEventListener('click', this._onClick);
      el.addEventListener('select', this._onSelect);
      el.addEventListener('scroll', this._onHighlightScroll);
      // (Re-)observe the new element for size changes.
      if (this._highlightResizeObserver) this._highlightResizeObserver.disconnect();
      if (typeof ResizeObserver !== 'undefined') {
        this._highlightResizeObserver = new ResizeObserver(() => this.syncHighlight());
        this._highlightResizeObserver.observe(el);
      }
    },
    detachInputListeners() {
      const el = this._listenedEl;
      if (!el) return;
      el.removeEventListener('focus', this._onFocus);
      el.removeEventListener('blur', this._onBlur);
      el.removeEventListener('keydown', this._onKeydown);
      el.removeEventListener('keyup', this._onKeyup);
      el.removeEventListener('click', this._onClick);
      el.removeEventListener('select', this._onSelect);
      el.removeEventListener('scroll', this._onHighlightScroll);
      this._listenedEl = null;
    },
    onInput(newValue) {
      this.$emit('input', newValue);
      this.$nextTick(() => {
        this.refreshCursor();
        if (this.showDropdown) this.positionDropdown();
      });
    },
    scrollActiveIntoView() {
      if (!this.$el) return;
      const active = this.$el.querySelector('.ve-input__item--active');
      if (active && typeof active.scrollIntoView === 'function') {
        // `nearest` skips no-op scrolls and avoids scrolling the page if already visible.
        active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    },
    // Copies position + text-rendering styles from the input to the overlay so the styled
    // spans line up character-for-character. Re-run on resize/scroll/value-change.
    syncHighlight() {
      const el = this.inputEl();
      const overlay = this.$refs.highlight;
      const wrapper = this.$el;
      if (!el || !overlay || !wrapper) return;
      const elRect = el.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      const cs = window.getComputedStyle(el);
      this.highlightStyle = {
        position: 'absolute',
        top: `${elRect.top - wrapperRect.top}px`,
        left: `${elRect.left - wrapperRect.left}px`,
        width: `${elRect.width}px`,
        height: `${elRect.height}px`,
        // Every property that affects character placement, so wrap points align.
        fontFamily: cs.fontFamily,
        fontSize: cs.fontSize,
        fontWeight: cs.fontWeight,
        fontStyle: cs.fontStyle,
        lineHeight: cs.lineHeight,
        letterSpacing: cs.letterSpacing,
        wordSpacing: cs.wordSpacing,
        tabSize: cs.tabSize,
        textIndent: cs.textIndent,
        paddingTop: cs.paddingTop,
        paddingRight: cs.paddingRight,
        paddingBottom: cs.paddingBottom,
        paddingLeft: cs.paddingLeft,
        borderTopWidth: cs.borderTopWidth,
        borderRightWidth: cs.borderRightWidth,
        borderBottomWidth: cs.borderBottomWidth,
        borderLeftWidth: cs.borderLeftWidth,
        borderColor: 'transparent',
        borderStyle: 'solid',
        boxSizing: cs.boxSizing,
        whiteSpace: this.isTextarea ? 'pre-wrap' : 'pre',
        // <input> vertically-centers its text; the overlay div needs flex to match. Textareas top-align.
        display: this.isTextarea ? 'block' : 'flex',
        alignItems: this.isTextarea ? undefined : 'center',
        wordBreak: cs.wordBreak,
        overflowWrap: cs.overflowWrap,
        overflow: 'hidden',
        pointerEvents: 'none',
        background: 'transparent', // textarea keeps its own background
        margin: 0,
        zIndex: 1, // sit on top of the textarea; pointer-events:none lets clicks pass through
        color: 'transparent', // base text invisible; per-span colors override
      };
      overlay.scrollTop = el.scrollTop;
      overlay.scrollLeft = el.scrollLeft;
    },
    positionDropdown() {
      const el = this.inputEl();
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const gap = 4; // small breathing room between input and dropdown
      const padding = 8; // keep some space from the viewport edge
      const preferredMax = 280;
      const spaceBelow = Math.max(0, viewportH - rect.bottom - padding);
      const spaceAbove = Math.max(0, rect.top - padding);
      // Prefer downward; flip up only when there's clearly more room above.
      const openDown = spaceBelow >= 160 || spaceBelow >= spaceAbove;
      const maxHeight = Math.min(preferredMax, openDown ? spaceBelow : spaceAbove);
      if (openDown) {
        this.dropdownStyle = {
          position: 'fixed',
          top: `${rect.bottom + gap}px`,
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          maxHeight: `${maxHeight}px`,
        };
      } else {
        this.dropdownStyle = {
          position: 'fixed',
          bottom: `${viewportH - rect.top + gap}px`,
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          maxHeight: `${maxHeight}px`,
        };
      }
    },
    onKeydown(event) {
      // Keep cursor in sync for caret-moving keys (arrows, home/end, etc.).
      this.$nextTick(() => this.refreshCursor());

      // Textarea Enter → newline. Parent form has @keydown.enter.prevent; stopPropagation lets it through.
      const dropdownActive = this.showDropdown && this.suggestions.length > 0;
      if (event.key === 'Enter' && this.isTextarea && !dropdownActive) {
        event.stopPropagation();
        return;
      }

      if (!dropdownActive) return;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.activeIndex = (this.activeIndex + 1) % this.suggestions.length;
          this.$nextTick(() => this.scrollActiveIntoView());
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.activeIndex = (this.activeIndex - 1 + this.suggestions.length) % this.suggestions.length;
          this.$nextTick(() => this.scrollActiveIntoView());
          break;
        case 'Enter':
        case 'Tab':
          event.preventDefault();
          this.applySuggestion(this.activeIndex);
          break;
        case 'Escape':
          event.preventDefault();
          this.focused = false;
          break;
      }
    },
    refreshCursor() {
      const el = this.inputEl();
      if (!el) return;
      const next = el.selectionStart != null ? el.selectionStart : (this.value || '').length;
      if (next !== this.cursor) this.cursor = next;
    },
    // Public — invoked from the parent's "Insert variable" button. Inserts `{{}}` at the cursor
    // (or replacing the selection) and lands the caret between the braces.
    insertVariable() {
      const el = this.inputEl();
      if (!el) return;
      el.focus();
      let inserted = false;
      try {
        inserted = document.execCommand('insertText', false, '{{}}');
      } catch {
        inserted = false;
      }
      if (inserted) {
        // execCommand fired the input event upstream already. Position caret between braces.
        const newPos = el.selectionStart - 2;
        el.setSelectionRange(newPos, newPos);
      } else {
        // Fallback: manual splice for environments without execCommand.
        const v = this.value || '';
        const start = el.selectionStart != null ? el.selectionStart : v.length;
        const end = el.selectionEnd != null ? el.selectionEnd : v.length;
        const next = v.slice(0, start) + '{{}}' + v.slice(end);
        this.$emit('input', next);
        this.$nextTick(() => {
          const e2 = this.inputEl();
          if (e2) {
            e2.focus();
            const newPos = start + 2;
            e2.setSelectionRange(newPos, newPos);
          }
        });
      }
      this.$nextTick(() => {
        this.refreshCursor();
        this.focused = true;
      });
    },
    applySuggestion(idx) {
      const item = this.suggestions[idx];
      const state = this.parseState;
      if (!item || !state) return;

      const el = this.inputEl();
      // execCommand('insertText') so Ctrl+Z reverts the insertion like typing.
      // Setting el.value via $emit → re-render wipes the undo history.
      if (el && typeof document !== 'undefined' && document.execCommand) {
        el.focus();
        el.setSelectionRange(state.replaceStart, state.replaceEnd);
        let inserted = false;
        try {
          inserted = document.execCommand('insertText', false, item.insertText);
        } catch {
          inserted = false;
        }
        if (inserted) {
          // execCommand fired a synchronous `input` event; parent v-model is already in sync.
          const offset = item.cursorOffset || 0;
          if (offset !== 0) {
            const pos = el.selectionStart + offset;
            el.setSelectionRange(pos, pos);
          }
          this.$nextTick(() => {
            this.refreshCursor();
            this.focused = true;
          });
          return;
        }
      }

      // Fallback when execCommand is unavailable: emit-based replace. Loses undo for this edit.
      const current = this.value || '';
      const before = current.substring(0, state.replaceStart);
      const after = current.substring(state.replaceEnd);
      const next = before + item.insertText + after;
      const newCursor = state.replaceStart + item.insertText.length + (item.cursorOffset || 0);
      this.$emit('input', next);
      this.$nextTick(() => {
        const e = this.inputEl();
        if (e) {
          e.focus();
          e.setSelectionRange(newCursor, newCursor);
        }
        this.cursor = newCursor;
        this.focused = true;
      });
    },
  },
};
</script>

<style lang="scss">
.ve-input {
  position: relative; // anchor for the syntax-highlight overlay

  // __dropdown positioning is set inline (position: fixed) to escape the dialog's overflow clip.

  // Hide the underlying text — the overlay paints the styled version — but keep the caret visible.
  .form-input__input {
    color: transparent !important;
    caret-color: $color-normal;
  }
  // Translucent neutral so the selection rectangle reads through the overlay text
  // without stamping a duplicate string underneath.
  .form-input__input::selection {
    color: transparent;
    background: rgba(0, 0, 0, 0.18);
  }

  &__highlight {
    // Positioning/font/padding are set inline via syncHighlight(); only sync-independent rules go here.
    z-index: 1;
  }

  &__highlight-dynamic {
    color: $color-accent;
    font-weight: 500;
  }

  // Literal (non-dynamic) spans.
  &__highlight > span:not(&__highlight-dynamic) {
    color: $color-normal;
  }

  &__dropdown {
    background: $color-white;
    border: 1px solid $color-border;
    border-radius: 6px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
    z-index: 2000; // above the Vuetify dialog (typically ~200) and the syntax-help drawer (1000)
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__dropdown-list {
    flex: 1 1 auto;
    overflow-y: auto;
    min-height: 0; // let the list shrink so the description footer can show.
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 12px;
    cursor: pointer;
    transition: background 80ms ease;

    &--active,
    &:hover {
      background: $color-neutral-pale;
    }
  }

  &__item-label {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 13px;
    color: $color-normal;
    background: none;
    border: none;
    padding: 0;
    flex: 1 1 auto; // grow to fill, pushing badge + type to the right edge
  }

  &__item-type {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 11px;
    color: $color-normal-pale;
  }

  &__item-badge {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: $color-yellow-pale;
    color: $color-yellow;
    padding: 1px 6px;
    border-radius: 10px;
  }

  &__description {
    border-top: 1px solid $color-border;
    padding: 8px 12px;
    background: $color-neutral-pale;
    font-size: 12px;
    color: $color-normal-pale;
    line-height: 1.4;

    &--hint {
      font-style: italic;
    }
  }
}
</style>
